export interface OrderPricingOption {
    title?: string;
    totalSteps: number;
    sortOrder?: number;
    price: number;
    originalPrice?: number;
    discountPercent?: number;
    discountedPrice?: number;
}

export interface ParsedOrderInput {
    code: string;
    totalSteps: number;
    hasValidFormat: boolean;
}

export interface OrderPriceEstimate {
    orderCount: number;
    originalTotal: number;
    payableTotal: number;
}

export const COMMUNITY_TICKET_NOTES: Record<number, string> = {
    4: 'Rương SS',
    8: 'Thiếu, đủ hoặc full 3 rương',
    9: 'Full 3 rương và dư một ít săn skin SSS',
};

export const getCommunityTicketNote = (pricing: OrderPricingOption): string | undefined =>
    pricing.totalSteps * 38 >= 1216
        ? pricing.title?.trim() || undefined
        : COMMUNITY_TICKET_NOTES[pricing.totalSteps];

export const formatCommunityTicketLabel = (pricing: OrderPricingOption): string => {
    const calculatedTickets = pricing.totalSteps * 38;
    const databaseTitle = getCommunityTicketNote(pricing);

    if (calculatedTickets >= 1216 && databaseTitle) {
        return databaseTitle;
    }

    const legacyNote = calculatedTickets < 1216
        ? COMMUNITY_TICKET_NOTES[pricing.totalSteps]
        : undefined;

    return `${calculatedTickets} vé${legacyNote ? ` (${legacyNote})` : ''}`;
};

export const parseBulkOrderInput = (orderCode: string, defaultSteps: number): ParsedOrderInput[] => {
    const lines = orderCode
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    return lines.map((line) => {
        const parts = line.split('|').map((part) => part.trim());
        const rawCode = parts[0] || '';
        const invitedCode = rawCode.match(/--(.*?)--/);
        const code = (invitedCode?.[1] || rawCode).trim();
        const totalSteps = parts.length === 1
            ? (lines.length === 1 ? defaultSteps : 1)
            : Number(parts[1]);

        return {
            code,
            totalSteps,
            hasValidFormat: parts.length <= 2 && Number.isInteger(totalSteps) && totalSteps > 0,
        };
    });
};

export const estimateBulkOrderPrice = (
    orderCode: string,
    defaultSteps: number,
    pricing: OrderPricingOption[],
): OrderPriceEstimate | undefined => {
    const orders = parseBulkOrderInput(orderCode, defaultSteps);
    if (
        orders.length === 0
        || orders.some((order) => !order.hasValidFormat || order.code.length !== 10)
        || pricing.length === 0
    ) {
        return undefined;
    }

    const singleStepPricing = pricing.find((item) => item.totalSteps === 1);
    let originalTotal = 0;
    let payableTotal = 0;

    for (const order of orders) {
        const exactPricing = pricing.find((item) => item.totalSteps === order.totalSteps);
        const priceOption = exactPricing || singleStepPricing;
        if (!priceOption) return undefined;

        const originalPrice = exactPricing
            ? (priceOption.originalPrice ?? priceOption.price)
            : (priceOption.originalPrice ?? priceOption.price) * order.totalSteps;
        const payablePrice = exactPricing
            ? (priceOption.discountedPrice ?? priceOption.price)
            : Math.max(0, originalPrice - Math.round((originalPrice * (priceOption.discountPercent ?? 0)) / 100));

        originalTotal += originalPrice;
        payableTotal += payablePrice;
    }

    return { orderCount: orders.length, originalTotal, payableTotal };
};
